import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, createStyles, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import * as ChatDAO from 'llm/chat-library/ChatDAO';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import * as Net from 'util/Net';

import {
  ONBOARDING_CHIP_STAGGER_MS,
  OnboardingSkipButton,
  OnboardingStaggeredEnterFade,
} from './OnboardingComponents';
import {ONBOARDING_SUBTITLE} from './onboardingCopy';
import {startOnboardingSession} from './OnboardingDAO';
import {
  OnboardingQuestionLoadingView,
  OnboardingQuestionView,
  OnboardingScreenShell,
} from './OnboardingQuestionView';

interface OnboardingQuestion {
  question: string;
  choices: Array<string>;
}

interface OnboardingAppState {
  is_complete?: boolean;
  questions_asked?: Array<OnboardingQuestion>;
}

function isOnboardingAppState(state: unknown): state is OnboardingAppState {
  if (!state || typeof state !== 'object') {
    return false;
  }
  const s = state as OnboardingAppState;
  return s.questions_asked != null || s.is_complete != null;
}

const SKIPPED_ANSWER = '[skipped]';

/** Decorative illustration pinned to the bottom of the onboarding error state only. */
const ONBOARDING_ERROR_SEA_ANEMONE_PATH = '/static/img/ai-chat-onboarding/onboarding-sea-anemone.png';
const ERROR_ILLUSTRATION_MAX_WIDTH_PX = 230;
/** Intrinsic asset size; used for aspect-ratio reservation so layout is stable before decode. */
const ERROR_ILLUSTRATION_INTRINSIC_WIDTH_PX = 736;
const ERROR_ILLUSTRATION_INTRINSIC_HEIGHT_PX = 546;
const ERROR_ILLUSTRATION_RESERVED_HEIGHT_PX = Math.round(
  ERROR_ILLUSTRATION_MAX_WIDTH_PX *
    (ERROR_ILLUSTRATION_INTRINSIC_HEIGHT_PX / ERROR_ILLUSTRATION_INTRINSIC_WIDTH_PX)
);

/** Poll while waiting for the agent to publish questions or advance the thread. */
const ONBOARDING_LOAD_POLL_MS = 2000;

const useStyles = createStyles((theme) => ({
  errorState: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  errorContent: {
    flex: 1,
    minHeight: 0,
    // Reserve illustration space up front so centered copy does not jump when the image loads.
    paddingBottom: ERROR_ILLUSTRATION_RESERVED_HEIGHT_PX,
  },
  loadingAnswerText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    textAlign: 'center',
    maxWidth: 280,
    wordWrap: 'break-word',
  },
  errorText: {
    padding: theme.spacing.xxl,
    width: '100%',
    color: theme.colors.gray[7],
  },
  errorActionFade: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  errorIllustrationSlot: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: `calc(-1 * ${theme.spacing.md})`,
    display: 'flex',
    justifyContent: 'center',
    height: ERROR_ILLUSTRATION_RESERVED_HEIGHT_PX,
    pointerEvents: 'none',
  },
  errorIllustration: {
    width: '100%',
    maxWidth: ERROR_ILLUSTRATION_MAX_WIDTH_PX,
    height: 'auto',
    display: 'block',
  },
}));

export function CyclingAnswersText({answers}: {answers: Array<string>}): JSX.Element | null {
  const {classes} = useStyles();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    if (answers.length <= 1) {
      return;
    }
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % answers.length);
    }, 1800);
    return () => {
      clearInterval(id);
    };
  }, [answers]);

  if (answers.length === 0) {
    return null;
  }

  return <Text className={classes.loadingAnswerText}>"{answers[index]}"</Text>;
}

function QuestionView({
  chatSessionId,
  questions,
  questionGroupIndex,
  progressCount,
  onAnswersSubmitted,
  onQuestionIndexChange,
  onQuestionAnswered,
  isExpanded = true,
}: {
  chatSessionId: string;
  questions: Array<OnboardingQuestion>;
  /** Zero-based index of this question group within the entire onboarding flow (increments each time the backend sends a new batch). */
  questionGroupIndex: number;
  /** Steps reached so far (across all batches), used to fill the progress bar. */
  progressCount: number;
  onAnswersSubmitted: (answers: Array<string>) => void;
  onQuestionIndexChange: (index: number) => void;
  /** Fired once per question the user resolves (answer or Skip), so the parent can tally progress. */
  onQuestionAnswered: () => void;
  isExpanded?: boolean;
}): JSX.Element {
  const [batchIndex, setBatchIndex] = useState(0);
  const [batchAnswers, setBatchAnswers] = useState<Array<string>>([]);
  // Multi-select: chips toggle independently and combine with the free-text value into one answer.
  // The user confirms with Continue (chips do not auto-advance), so several options can be chosen.
  const [selectedChoices, setSelectedChoices] = useState<Set<string>>(new Set());
  const [freeTextValue, setFreeTextValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset local step state when the backend sends a new question batch.
  useEffect(() => {
    setBatchIndex(0);
    setBatchAnswers([]);
    setSelectedChoices(new Set());
    setFreeTextValue('');
    setIsSubmitting(false);
  }, [questions]);

  useEffect(() => {
    onQuestionIndexChange(batchIndex);
  }, [batchIndex, onQuestionIndexChange]);

  const currentQuestion = questions[batchIndex] ?? null;
  const freeTextTrimmed = freeTextValue.trim();
  // Selected chips and the free-text value combine into a single answer string for this question.
  const effectiveAnswer = [...selectedChoices, ...(freeTextTrimmed ? [freeTextTrimmed] : [])].join(', ');
  const canContinue = !isSubmitting && effectiveAnswer.length > 0;

  const handleSelectChoice = useCallback((choice: string) => {
    setSelectedChoices((prev) => {
      const next = new Set(prev);
      if (next.has(choice)) {
        next.delete(choice);
      } else {
        next.add(choice);
      }
      return next;
    });
  }, []);

  const handleDiscardFreeText = useCallback(() => {
    setFreeTextValue('');
  }, []);

  const submitAnswers = useCallback(
    async (answers: Array<string>) => {
      setIsSubmitting(true);
      onAnswersSubmitted(answers);
      try {
        const messageText = questions.map((q, i) => `${q.question}: ${answers[i] ?? ''}`).join('\n');
        await ChatDAO.appendDisplayMessageToChatSession({
          chatSessionId,
          agentType: AgentType.ONBOARDING,
          messageBlocks: [{type: 'text', text: messageText}],
          userChatContext: ChatDAO.getUserChatContext({contextIds: []}),
          sources: [],
        });
      } catch (err) {
        if (!Net.isServerRequestError(err)) {
          throw err;
        }
        setIsSubmitting(false);
      }
    },
    [chatSessionId, questions, onAnswersSubmitted]
  );

  const handleAdvance = useCallback(
    async (answer: string) => {
      const isSkipped = answer === SKIPPED_ANSWER;

      if (isSkipped) {
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SKIPPED_ONBOARDING_QUESTION, {
          question_index_in_group: batchIndex,
          question_group_index: questionGroupIndex,
          question_group_size: questions.length,
          num_choices: currentQuestion.choices.length,
        });
      } else {
        // The answer may combine several picked chips and/or a free-text value.
        const hasFreeText = freeTextValue.trim().length > 0;
        const selectedChoiceCount = selectedChoices.size;
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.ANSWERED_ONBOARDING_QUESTION, {
          question_index_in_group: batchIndex,
          question_group_index: questionGroupIndex,
          question_group_size: questions.length,
          num_choices: currentQuestion.choices.length,
          selected_choice_count: selectedChoiceCount,
          has_free_text_answer: hasFreeText,
          total_answer_count: selectedChoiceCount + (hasFreeText ? 1 : 0),
        });
      }

      // Tally one resolved question (answered or skipped) toward the progress bar.
      onQuestionAnswered();

      const updatedAnswers = [...batchAnswers, answer];
      const nextIndex = batchIndex + 1;

      if (nextIndex < questions.length) {
        setBatchAnswers(updatedAnswers);
        setBatchIndex(nextIndex);
        setSelectedChoices(new Set());
        setFreeTextValue('');
        return;
      }

      await submitAnswers(updatedAnswers);
    },
    [
      batchAnswers,
      batchIndex,
      questions,
      questionGroupIndex,
      freeTextValue,
      selectedChoices,
      submitAnswers,
      currentQuestion,
      onQuestionAnswered,
    ]
  );

  if (!currentQuestion) {
    return <></>;
  }

  return (
    <OnboardingQuestionView
      testComponent="AIOnboardingScreen"
      question={currentQuestion.question}
      subtitle={ONBOARDING_SUBTITLE}
      progressCount={progressCount}
      isExpanded={isExpanded}
      choices={currentQuestion.choices.map((choice) => ({
        label: choice,
        selected: selectedChoices.has(choice),
        // Chips toggle independently so the user can pick several; Continue submits the combined answer.
        onSelect: () => {
          if (isSubmitting) {
            return;
          }
          handleSelectChoice(choice);
        },
      }))}
      freeText={{
        value: freeTextValue,
        onChange: setFreeTextValue,
        onDiscard: handleDiscardFreeText,
      }}
      footer={
        <Stack spacing="sm" align="center">
          {/* Continue submits the combined chips + free-text answer once at least one is present. */}
          <Button
            variant="primary"
            size="sm"
            radius="xl"
            onClick={() => effectiveAnswer && handleAdvance(effectiveAnswer)}
            disabled={!canContinue}
            {...getTestProps({component: 'AIOnboardingScreen', element: 'continue-button'})}
          >
            Continue
          </Button>
          <OnboardingSkipButton
            onClick={() => handleAdvance(SKIPPED_ANSWER)}
            disabled={isSubmitting}
            testComponent="AIOnboardingScreen"
          />
        </Stack>
      }
    />
  );
}

/**
 * Onboarding screen driven directly by useMessageThread.
 *
 * Thread status drives UI states:
 *  - loading skeleton while the agent runs or application state is not ready yet
 *  - isAwaitingUserInput + questions_asked → question UI
 *  - errorMessage (including thread ERROR) → error UI with optional retry
 */
export function AIOnboardingScreen({
  chatSessionId,
  onChatSessionIdChange,
  onRetry,
  onComplete,
  isExpanded = true,
}: {
  chatSessionId: string;
  /** Sync when the backend returns a different session id (e.g. after a server-side reset). */
  onChatSessionIdChange?: (chatSessionId: string) => void;
  /** Restart the onboarding flow after a thread error. */
  onRetry?: () => void;
  /**
   * Called when the agent survey finishes — either by completing naturally or by the user exiting
   * early. Receives the number of questions answered so far so the next onboarding step (e.g. the
   * interest/first-chat screen) can continue the progress dots. Analytics for completion vs.
   * dismissal are fired before this is invoked.
   */
  onComplete: (answeredCount: number) => void;
  /** When false, pin the survey unit to the minimized top inset. */
  isExpanded?: boolean;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const {isAwaitingUserInput, isCompleted, latestApplicationState, errorMessage, refetch} = useMessageThread({
    chatSessionId,
  });

  const [submittedAnswers, setSubmittedAnswers] = useState<Array<string>>([]);

  // Number of questions the user has resolved (answered or skipped) across all batches. Held here so
  // it accumulates even as QuestionView remounts per backend batch; drives the progress dots.
  const [answeredCount, setAnsweredCount] = useState(0);
  const handleQuestionAnswered = useCallback(() => {
    setAnsweredCount((prev) => prev + 1);
  }, []);

  // Track how many question batches have been shown (increments each time a new batch arrives).
  const [questionGroupIndex, setQuestionGroupIndex] = useState(0);
  const lastQuestionsRef = useRef<Array<OnboardingQuestion> | null>(null);
  const totalQuestionsShownRef = useRef(0);
  const currentQuestionIndexInGroupRef = useRef(0);

  const handleQuestionIndexChange = useCallback((index: number) => {
    currentQuestionIndexInGroupRef.current = index;
  }, []);

  // Questions are available in application state while AWAITING_USER_INPUT.
  const currentQuestions = useMemo((): Array<OnboardingQuestion> | null => {
    if (!isAwaitingUserInput || !isOnboardingAppState(latestApplicationState)) {
      return null;
    }
    const questions = latestApplicationState.questions_asked;
    if (!Array.isArray(questions) || questions.length === 0) {
      return null;
    }
    return questions;
  }, [isAwaitingUserInput, latestApplicationState]);

  const isQuestionReady = isAwaitingUserInput && currentQuestions != null;

  // Thread status and state-update events can land in separate responses; poll until questions
  // are ready, the thread completes, or an error is surfaced. Re-call /start on the same cadence
  // so a COMPLETED thread re-queues onboarding_agent_task if the worker missed the first run.
  useEffect(() => {
    if (errorMessage != null || isQuestionReady || isCompleted) {
      return undefined;
    }
    const syncSessionAndPoll = (): void => {
      refetch();
      startOnboardingSession()
        .then(({session_id: sessionId}) => {
          if (sessionId !== chatSessionId) {
            onChatSessionIdChange?.(sessionId);
          }
        })
        .catch((err: unknown) => {
          if (!Net.isServerRequestError(err)) {
            throw err;
          }
        });
    };
    syncSessionAndPoll();
    const intervalId = window.setInterval(syncSessionAndPoll, ONBOARDING_LOAD_POLL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [errorMessage, isQuestionReady, isCompleted, refetch, chatSessionId, onChatSessionIdChange]);

  // Increment the question group counter when a new batch of questions arrives.
  useEffect(() => {
    if (currentQuestions == null || currentQuestions === lastQuestionsRef.current) {
      return;
    }
    if (lastQuestionsRef.current != null) {
      setQuestionGroupIndex((prev) => prev + 1);
    }
    totalQuestionsShownRef.current += currentQuestions.length;
    currentQuestionIndexInGroupRef.current = 0;
    lastQuestionsRef.current = currentQuestions;
  }, [currentQuestions]);

  // Track when the onboarding screen is first shown.
  useEffect(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_ONBOARDING_SCREEN);
  }, []);

  // Warm the error illustration so it is likely cached before the error state renders.
  useEffect(() => {
    if (typeof Image === 'undefined') {
      return;
    }
    const preload = new Image();
    preload.src = staticURL(ONBOARDING_ERROR_SEA_ANEMONE_PATH);
  }, []);

  // When the thread completes, fire a completion event then advance the onboarding flow.
  useEffect(() => {
    if (isCompleted) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.COMPLETED_ONBOARDING);
      onComplete(answeredCount);
    }
    // answeredCount is intentionally read at completion time only; including it would re-fire on
    // every answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleted, onComplete]);

  const handleDismiss = useCallback(() => {
    if (!isCompleted) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.DISMISSED_ONBOARDING_SCREEN, {
        question_group_index: questionGroupIndex,
        question_index_in_group: currentQuestionIndexInGroupRef.current,
        questions_shown_total: totalQuestionsShownRef.current,
      });
    }
    onComplete(answeredCount);
  }, [isCompleted, onComplete, questionGroupIndex, answeredCount]);

  const hasPreviousQuestions = answeredCount > 0 || submittedAnswers.length > 0;
  const loadingMessage = hasPreviousQuestions
    ? 'Processing your answers…'
    : 'Starting onboarding and gathering information…';

  return (
    <OnboardingScreenShell testComponent="AIOnboardingScreen" onExit={handleDismiss}>
      {errorMessage != null ? (
        <Box className={classes.errorState}>
          <Stack align="center" justify="center" spacing="sm" className={classes.errorContent}>
            <OnboardingStaggeredEnterFade animationKey={errorMessage} variant="fade">
              <Text size="lg" align="center" className={classes.errorText}>
                {errorMessage}
              </Text>
            </OnboardingStaggeredEnterFade>
            {onRetry != null && (
              <OnboardingStaggeredEnterFade
                animationKey={errorMessage}
                delayMs={ONBOARDING_CHIP_STAGGER_MS}
                variant="fade"
              >
                <Box className={classes.errorActionFade}>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onRetry}
                    {...getTestProps({component: 'AIOnboardingScreen', element: 'error-retry-button'})}
                  >
                    Try again
                  </Button>
                </Box>
              </OnboardingStaggeredEnterFade>
            )}
            <OnboardingStaggeredEnterFade
              animationKey={errorMessage}
              delayMs={onRetry != null ? ONBOARDING_CHIP_STAGGER_MS * 2 : ONBOARDING_CHIP_STAGGER_MS}
              variant="fade"
            >
              <Box className={classes.errorActionFade}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  {...getTestProps({component: 'AIOnboardingScreen', element: 'error-exit-button'})}
                >
                  Exit
                </Button>
              </Box>
            </OnboardingStaggeredEnterFade>
          </Stack>
          <Box className={classes.errorIllustrationSlot} aria-hidden={true}>
            <img
              src={staticURL(ONBOARDING_ERROR_SEA_ANEMONE_PATH)}
              alt=""
              width={ERROR_ILLUSTRATION_INTRINSIC_WIDTH_PX}
              height={ERROR_ILLUSTRATION_INTRINSIC_HEIGHT_PX}
              className={classes.errorIllustration}
              {...getTestProps({component: 'AIOnboardingScreen', element: 'error-illustration'})}
            />
          </Box>
        </Box>
      ) : isQuestionReady ? (
        <QuestionView
          key={currentQuestions[0].question}
          chatSessionId={chatSessionId}
          questions={currentQuestions}
          questionGroupIndex={questionGroupIndex}
          // Count the question currently on screen as a reached step, so the bar shows from step 1.
          progressCount={answeredCount + 1}
          onAnswersSubmitted={setSubmittedAnswers}
          onQuestionIndexChange={handleQuestionIndexChange}
          onQuestionAnswered={handleQuestionAnswered}
          isExpanded={isExpanded}
        />
      ) : (
        <OnboardingQuestionLoadingView message={loadingMessage} isExpanded={isExpanded}>
          {submittedAnswers.length > 0 && <CyclingAnswersText answers={submittedAnswers} />}
        </OnboardingQuestionLoadingView>
      )}
    </OnboardingScreenShell>
  );
}
